import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Security questions state
  const [showQuestions, setShowQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      const data = response.data;

      if (data.has_security_questions) {
        setQuestions(data.questions);
        setShowQuestions(true);
        toast.info('Please answer your security questions');
      } else if (data.reset_token) {
        toast.success('Redirecting to password reset...');
        navigate(`/reset-password?token=${data.reset_token}`);
      } else {
        setSubmitted(true);
        toast.success('Check your email for reset instructions');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-security-questions`, { email, answers });
      const data = response.data;

      if (data.reset_token) {
        toast.success('Verified! Redirecting to reset password...');
        navigate(`/reset-password?token=${data.reset_token}`);
      } else if (data.email_sent) {
        setSubmitted(true);
        setShowQuestions(false);
        toast.success('Verified! Check your email for the reset link.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Security answers incorrect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="forgot-password-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/careguard-logo.png" alt="CareGuard" style={{ height: '60px', marginBottom: '1rem', borderRadius: '8px' }} />
          <h1>Forgot Password</h1>
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><i className="fa-solid fa-envelope-circle-check" style={{ color: '#2c5364' }}></i></div>
            <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}>Check Your Email</h2>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>
              If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p style={{ color: '#6b8899', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              The link will expire in 1 hour. Don't forget to check your spam folder!
            </p>
            <button onClick={() => { setSubmitted(false); setEmail(''); setShowQuestions(false); setAnswers({}); }}
              className="submit-button" style={{ marginBottom: '1rem' }}>Try Different Email</button>
            <div><Link to="/auth" style={{ color: '#2c5364', textDecoration: 'none' }}>Back to Login</Link></div>
          </div>
        ) : showQuestions ? (
          <div data-testid="security-questions-section">
            <p style={{ color: '#6b8899', marginBottom: '1.5rem', textAlign: 'center' }}>
              <i className="fa-solid fa-shield-halved" style={{ color: '#2c5364', marginRight: '6px' }}></i>
              Answer your security questions to verify your identity
            </p>
            <form onSubmit={handleQuestionsSubmit} className="auth-form">
              {questions.map(q => (
                <div className="form-group" key={q.num}>
                  <label style={{ fontWeight: '600', color: '#2c5364' }}>{q.question}</label>
                  <input
                    type="text"
                    value={answers[q.num] || ''}
                    onChange={e => setAnswers({ ...answers, [q.num]: e.target.value })}
                    placeholder="Your answer"
                    required
                    data-testid={`security-answer-${q.num}`}
                  />
                </div>
              ))}
              <button type="submit" className="submit-button" disabled={loading} data-testid="verify-answers-btn">
                {loading ? 'Verifying...' : 'Verify & Reset Password'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button type="button" onClick={() => { setShowQuestions(false); setAnswers({}); }}
                  style={{ background: 'none', border: 'none', color: '#2c5364', cursor: 'pointer', textDecoration: 'underline' }}>
                  Back
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={handleEmailSubmit} className="auth-form">
            <p style={{ color: '#6b8899', marginBottom: '1rem', textAlign: 'center' }}>Enter your email to reset your password</p>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email" required data-testid="email-input" />
            </div>
            <button type="submit" className="submit-button" disabled={loading} data-testid="submit-button">
              {loading ? 'Sending...' : 'Reset Password'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <Link to="/auth" style={{ color: '#2c5364', textDecoration: 'none' }}>Back to Login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
